import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import * as THREE from 'three'
import {
  deformarPonto,
  deslocamentoNoPonto,
  respostaPorPecas,
  semDeformacao,
  type CamadaDeformacao,
  type DeformacaoPeca,
  type EstadoDeformacaoVeiculo,
} from '../../dominio/didatico/campoDeformacao'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import { aplicarCampoNaGeometria, type TransformacaoRepouso } from './malhaDeformavel'

/**
 * Deformação de UM veículo, disponível para todas as peças dele.
 *
 * Cada veículo da cena monta o seu provedor: o carro A e o carro B nunca leem
 * o estado um do outro, e fora da Cinemática não há provedor — o veículo é
 * desenhado exatamente como sempre foi.
 */
interface ValorDeformacao {
  estado: EstadoDeformacaoVeiculo
  perfil: PerfilVeiculo
  pecas: Record<string, DeformacaoPeca>
  /**
   * Lataria sólida: a estrutura deformada (longarinas, colunas, soleiras) é
   * desenhada também "em raio-X", por cima da chapa, para o dano estrutural
   * não sumir atrás dela.
   */
  raioXEstrutural: boolean
}

const ContextoDeformacao = createContext<ValorDeformacao | null>(null)

export function ProvedorDeformacao({
  estado,
  perfil,
  raioXEstrutural = false,
  children,
}: {
  estado: EstadoDeformacaoVeiculo | null | undefined
  perfil: PerfilVeiculo
  raioXEstrutural?: boolean
  children: ReactNode
}) {
  const chave = estado?.chave ?? ''
  // memorizado pela chave: depois do esmagamento, o estado não muda mais
  const valor = useMemo<ValorDeformacao | null>(() => {
    if (!estado || semDeformacao(estado)) return null
    return { estado, perfil, pecas: respostaPorPecas(estado, perfil), raioXEstrutural }
  }, [chave, perfil, estado?.veiculoId, raioXEstrutural])
  return <ContextoDeformacao.Provider value={valor}>{children}</ContextoDeformacao.Provider>
}

export const useDeformacao = (): ValorDeformacao | null => useContext(ContextoDeformacao)

export function useDeformacaoPeca(pecaId: string): DeformacaoPeca | null {
  return useContext(ContextoDeformacao)?.pecas[pecaId] ?? null
}

/**
 * Geometria deformada de uma peça.
 *
 * Sem deformação devolve a própria base (que pode ser compartilhada). Com
 * deformação, clona a base UMA vez por instância e reescreve as posições a
 * cada mudança de estado — sem alocar geometria nova a cada quadro.
 */
export function useGeometriaDeformada<T extends THREE.BufferGeometry>(
  base: T,
  camada: CamadaDeformacao,
  repouso: TransformacaoRepouso,
): T {
  const ctx = useContext(ContextoDeformacao)
  const ativo = ctx !== null
  const clone = useMemo(() => (ativo ? (base.clone() as T) : null), [base, ativo])
  useEffect(() => () => clone?.dispose(), [clone])
  const chaveRepouso = [...repouso.posicao, ...(repouso.rotacao ?? []), ...(repouso.quaternion ?? [])].join(',')
  useMemo(() => {
    if (clone && ctx) aplicarCampoNaGeometria(base, clone, ctx.estado, ctx.perfil, camada, repouso)
  }, [clone, ctx, camada, chaveRepouso])
  return clone ?? base
}

/**
 * Mapeamento de pontos para peças descritas por pontos (barras da estrutura).
 *
 * `origem` é a translação do grupo em que as barras vivem, no referencial do
 * veículo: as barras da porta são desenhadas relativas à dobradiça.
 */
interface ValorMapeamento {
  mapear: (p: Vetor3) => Vetor3
  deslocamento: (p: Vetor3) => Vetor3
  /** desenhar por cima da chapa o que esta estrutura deformou (lataria sólida) */
  raioX: boolean
}

const ContextoMapeamento = createContext<ValorMapeamento | null>(null)

export function MapeamentoEstrutural({
  camada = 'estrutura',
  origem = [0, 0, 0],
  children,
}: {
  camada?: CamadaDeformacao
  origem?: readonly [number, number, number]
  children: ReactNode
}) {
  const ctx = useContext(ContextoDeformacao)
  const [ox, oy, oz] = origem
  const valor = useMemo<ValorMapeamento | null>(() => {
    if (!ctx) return null
    return {
      mapear: (p) => {
        const q = deformarPonto(ctx.estado, ctx.perfil, vetor(p.x + ox, p.y + oy, p.z + oz), camada)
        return vetor(q.x - ox, q.y - oy, q.z - oz)
      },
      deslocamento: (p) => deslocamentoNoPonto(ctx.estado, ctx.perfil, vetor(p.x + ox, p.y + oy, p.z + oz), camada),
      raioX: ctx.raioXEstrutural && camada === 'estrutura',
    }
  }, [ctx, camada, ox, oy, oz])
  return <ContextoMapeamento.Provider value={valor}>{children}</ContextoMapeamento.Provider>
}

export const useMapeamentoEstrutural = (): ValorMapeamento | null => useContext(ContextoMapeamento)

/**
 * Peça rígida deslocada pelo campo: gira em torno do próprio ponto de
 * ancoragem e some quando o domínio diz que ela não está mais visível.
 */
export function TransformacaoPeca({
  pecaId,
  ancora,
  children,
}: {
  pecaId: string
  ancora: readonly [number, number, number]
  children: ReactNode
}) {
  const peca = useDeformacaoPeca(pecaId)
  if (!peca) return <>{children}</>
  if (!peca.visivel) return null
  const [ax, ay, az] = ancora
  const [tx, ty, tz] = peca.translacao
  return (
    <group position={[ax + tx, ay + ty, az + tz]} rotation={peca.rotacao}>
      <group position={[-ax, -ay, -az]}>{children}</group>
    </group>
  )
}

/** Translação rígida pelo campo num ponto (painéis e caixas que não dobram). */
export function useDeslocamentoRigido(ponto: readonly [number, number, number], camada: CamadaDeformacao = 'estrutura'): [number, number, number] {
  const ctx = useContext(ContextoDeformacao)
  if (!ctx) return [0, 0, 0]
  const d = deslocamentoNoPonto(ctx.estado, ctx.perfil, vetor(...ponto), camada)
  return [d.x, d.y, d.z]
}

/**
 * Malha de uma geometria base (cacheada) que acompanha o campo quando há
 * colisão. Usada nos vidros: eles seguem colunas e teto em vez de ficarem
 * flutuando acima de um teto rebaixado.
 */
export function MalhaDeformavel({
  base,
  camada,
  posicao = [0, 0, 0],
  rotacao,
  children,
}: {
  base: THREE.BufferGeometry
  camada: CamadaDeformacao
  posicao?: readonly [number, number, number]
  rotacao?: readonly [number, number, number]
  children: ReactNode
}) {
  const geometria = useGeometriaDeformada(base, camada, { posicao, rotacao })
  return (
    <mesh
      geometry={geometria}
      position={posicao as [number, number, number]}
      rotation={rotacao as [number, number, number] | undefined}
    >
      {children}
    </mesh>
  )
}
